/**
 * Console Product — the market ladder and the mobile buy bar.
 *
 * product-detail.js owns the page; this adds the two things the Console boards
 * put on it that the legacy page has no equivalent for, and stays out of
 * everything else. Both are driven from data already on the page.
 *
 * THE LADDER IS ASKING PRICES ONLY. Every row is a live listing's asking
 * price, and the dashed marker is the 30-day median of asking prices from
 * MarketPriceStat. Nothing here is a sold or completed price — eBay's API
 * licence forbids presenting those, and the site does not store them
 * (docs/EBAY-DATA-RETENTION.md). The wording follows: "typical ask", never
 * "typical price", and never "sells for".
 */

const NUM = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function money(value) {
  return `$${NUM(value)}`;
}

function esc(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
}

/**
 * One ladder row.
 *
 * `pct` is the price's position within the span the ladder covers, so the bars
 * compare listings to each other rather than to zero — at these prices a
 * zero-based bar would make every listing look identical.
 */
function ladderRow({ price, pct, seller, tier, mine, ask }) {
  const classes = ['lad', mine ? 'me' : '', ask ? 'ask' : ''].filter(Boolean).join(' ');
  const meter = ask || tier == null
    ? '<span></span>'
    : `<span class="meter t${tier}" aria-hidden="true">`
      + `<i class="${tier >= 1 ? 'on' : ''}"></i><i class="${tier >= 2 ? 'on' : ''}"></i>`
      + `<i class="${tier >= 3 ? 'on' : ''}"></i></span>`;
  return `<div class="${classes}">`
    + `<span class="p">${esc(money(price))}</span>`
    + `<span class="bar"><i style="width:${pct}%"></i></span>`
    + `<span class="s">${esc(seller || '')}</span>`
    + meter
    + '</div>';
}

/**
 * Draw the ladder into `container` from a list of {price, seller, tier, mine}
 * plus an optional typical-ask median.
 *
 * Exported and DOM-free apart from the write, so the ordering and the bar
 * arithmetic can be tested without a page.
 */
export function ladderHtml(rows, typicalAsk = null) {
  const priced = rows.filter((row) => Number.isFinite(row.price) && row.price > 0);
  if (!priced.length) return '';

  const all = priced.map((row) => row.price);
  if (Number.isFinite(typicalAsk) && typicalAsk > 0) all.push(typicalAsk);
  const low = Math.min(...all);
  const high = Math.max(...all);
  const span = high - low;
  // A single price (or several identical ones) has no range to place anything
  // in; fill the bar rather than dividing by zero.
  const place = (value) => (span > 0 ? Math.round(((value - low) / span) * 82) + 18 : 100);

  const entries = priced.map((row) => ({ ...row, sort: row.price }));
  if (Number.isFinite(typicalAsk) && typicalAsk > 0) {
    entries.push({
      price: typicalAsk,
      sort: typicalAsk,
      ask: true,
      seller: 'typical ask · 30-day median of asking prices',
    });
  }
  entries.sort((a, b) => a.sort - b.sort);

  return '<div class="ladder">'
    + entries.map((row) => ladderRow({ ...row, pct: place(row.price) })).join('')
    + '</div>';
}

/**
 * Mirror the buy panel into a bar fixed to the bottom of a phone screen.
 *
 * Read from the panel rather than rendered a second time in the template: one
 * price and one link, so they cannot disagree and a screen reader is not told
 * about two "Buy on eBay" links. CSS decides when it is visible.
 */
function buildBuyBar() {
  const box = document.getElementById('buyBox');
  if (!box) return;
  const price = box.querySelector('.buy-box-price');
  const cta = box.querySelector('.buy-box-button');
  if (!price || !cta) return;

  let bar = document.getElementById('consoleBuyBar');
  if (!bar) {
    bar = document.createElement('div');
    bar.id = 'consoleBuyBar';
    bar.className = 'buybar';
    // Not a landmark and not announced: everything in it is a copy of what is
    // already in the buy panel above.
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
  }
  bar.innerHTML = `<span class="p">${esc(price.textContent.trim())}</span>`
    + `<a class="btn" href="${esc(cta.getAttribute('href') || '#')}" tabindex="-1"`
    + ' target="_blank" rel="noopener noreferrer sponsored">'
    + `${esc(cta.textContent.trim())}</a>`;
}

function init() {
  if (!document.body || !document.body.classList.contains('console')) return;
  if (!document.getElementById('buyBox')) return;   // not the product page

  buildBuyBar();
  // product-detail.js replaces the whole buy panel on hydration, so the bar is
  // rebuilt when it does.
  const box = document.getElementById('buyBox');
  new MutationObserver(buildBuyBar).observe(box, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
